import map from 'lodash/map';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Alert from '@spothero/ui/alert';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import Loader from '@spothero/ui/loader';
import Radio from '@spothero/ui/radio';
import RadioGroup from '@spothero/ui/radio-group';
import TextButton from '@spothero/ui/text-button';
import Tooltip from '@spothero/ui/tooltip';
import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import OversizedFeeAPI from 'api/oversized-fee';
import AddNewVehicleForm from './AddNewVehicleForm';

export default class VehicleList extends Component {
    static propTypes = {
        user: PropTypes.object,
        facilityId: PropTypes.number,
        make: PropTypes.string,
        model: PropTypes.string,
        year: PropTypes.string,
        phoneNumber: PropTypes.string,
        phoneNumberFriendly: PropTypes.string,
        edit: PropTypes.bool,
        isParentVisible: PropTypes.bool,
        required: PropTypes.bool,
        checkOversized: PropTypes.bool,
        feeMessage: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool
        ]),
        oversizedFeeType: PropTypes.string,
        defaultFeeMessage: PropTypes.string,
        iDontKnowChecked: PropTypes.bool,
        vehicleNotAllowed: PropTypes.bool,
        showUnknownChoices: PropTypes.bool,
        showSaveAsDefault: PropTypes.bool,
        loadVehicleDataOnMount: PropTypes.bool,
        allowManualEntry: PropTypes.bool,
        newVehicleFormErrors: PropTypes.array,
        onOversizedFeeUpdated: PropTypes.func,
        onIDontKnowChanged: PropTypes.func,
        onShowAddNew: PropTypes.func,
        onHideAddNew: PropTypes.func
    };
    static defaultProps = {
        edit: true,
        required: true,
        oversizedFeeType: 'class',
        defaultFeeMessage: 'Oversized fees',
        showUnknownChoices: true,
        showSaveAsDefault: true
    };

    constructor(props) {
        super(props);

        const {
            edit,
            iDontKnowChecked,
            feeMessage,
            defaultFeeMessage,
            vehicleNotAllowed,
            user,
            showUnknownChoices,
            make,
            model,
            year
        } = props;
        const {
            make: newMake,
            model: newModel,
            year: newYear,
            vehicles,
            showAddNew
        } = this._getUserVehicles({user, showUnknownChoices, iDontKnowChecked, make, model, year});

        this._isMobile = EnvironmentUtils.isMobileDevice();
        this._warningDismissed = this._isMobile;
        this._requiredErrorMessage = 'Vehicle selection is required.';
        this._yearsByModel = [];

        this.state = {
            vehicles,
            make: newMake,
            model: newModel,
            year: newYear,
            edit,
            iDontKnowChecked,
            feeMessage,
            requiredError: null,
            showWarningTooltip: false,
            highlight: false,
            fee: null,
            feeError: null,
            apiFailed: false,
            feeMessageLoading: false,
            defaultFeeMessage,
            vehicleNotAllowed,
            showAddNew
        };
    }

    componentDidMount() {
        const {
            loadVehicleDataOnMount,
            checkOversized
        } = this.props;

        if (loadVehicleDataOnMount) {
            const {
                make,
                model,
                year
            } = this.state;

            if (!make && !model && !year) { return; }

            if (checkOversized) {
                this._loadOversizedFee({make, model, year});
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.showAddNew && !this.state.showAddNew) {
            const {
                onHideAddNew
            } = this.props;

            if (onHideAddNew) {
                onHideAddNew();
            }
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        if (this._feeLoader) {
            this._feeLoader.cancel();
        }
    }

    _onWarningTooltipHidden = () => {
        this._warningDismissed = true;

        this.setState({
            showWarningTooltip: false
        });
    }

    _onNewVehicleSelectChange = ({make, model, year}) => {
        this.setState({
            make,
            model,
            year,
            requiredError: null
        });
    }

    _onVehicleChange = evt => {
        const {
            onShowAddNew,
            checkOversized
        } = this.props;
        const value = evt.target.value;

        if (value === 'DONTKNOW') {
            this._onIDontKnowSelected();
        } else if (value === 'new') {
            this.setState({
                make: null,
                model: null,
                year: null,
                iDontKnowChecked: false,
                fee: null,
                feeMessage: null,
                feeError: null,
                showAddNew: true,
                showWarningTooltip: false
            });

            if (onShowAddNew) {
                onShowAddNew();
            }
        } else if (checkOversized) {
            const values = value.split(' ');

            this._loadOversizedFee({
                make: values[1],
                model: values[2],
                year: values[0]
            });
        } else {
            const values = value.split(' ');

            this.setState({
                showAddNew: false,
                make: values[1],
                model: values[2],
                year: values[0]
            });
        }
    }

    _onVehicleDataRetrieved = data => {
        const {
            make,
            model,
            year
        } = data;
        const newState = data;

        if (make || model || year) {
            newState.iDontKnowChecked = false;
        }

        if (this.props.checkOversized && make && model && year) {
            this._loadOversizedFee(data, false);
        } else {
            newState.fee = null;
        }

        this.setState(newState);
    }

    _onIDontKnowSelected() {
        const {
            onOversizedFeeUpdated,
            checkOversized
        } = this.props;
        const newState = {
            make: null,
            model: null,
            year: null,
            iDontKnowChecked: true,
            fee: null,
            feeMessage: null,
            feeError: null,
            requiredError: null,
            highlight: false,
            showAddNew: false
        };

        if (!this._warningDismissed) {
            newState.showWarningTooltip = true;
        }

        this.setState(newState);

        if (checkOversized && onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response: false,
                state: newState
            });
        }
    }

    _onEditClick = evt => {
        evt.preventDefault();

        this.setState({
            edit: true
        });
    }

    _getUserVehicles({user, showUnknownChoices, iDontKnowChecked, make, model, year}) {
        const showAddNew = !((user.vehicles && user.vehicles.length > 0) || iDontKnowChecked);
        const vehicles = (user.vehicles) ? user.vehicles : [];
        let newMake = make;
        let newModel = model;
        let newYear = year;

        const initialVehicles = map(vehicles, vehicle => {
            if (!newMake && vehicle.isDefault) {
                newMake = vehicle.make;
                newModel = vehicle.model;
                newYear = vehicle.year;
            }

            return {
                label: `${vehicle.year} ${vehicle.makeLabel} ${vehicle.modelLabel}`,
                name: vehicle.name,
                value: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                checked: (newMake === vehicle.make && newModel === vehicle.model && newYear === vehicle.year)
            };
        });

        if (showUnknownChoices) {
            initialVehicles.push({
                label: `I don't know my vehicle details right now`,
                value: 'DONTKNOW',
                checked: iDontKnowChecked,
                className: 'VehicleList-dont-know'
            });
        }

        initialVehicles.push({
            label: 'Add new vehicle',
            value: 'new',
            checked: showAddNew
        });

        if (filter(initialVehicles, {checked: true}).length === 0) {
            // there are no checked vehicles, this may happen if the user deleted their vehicle in their account and its
            // still showing up on parking pass or similar so we select the first one available
            const values = initialVehicles[0].value.split(' ');

            initialVehicles[0].checked = true;
            newMake = values[1];
            newModel = values[2];
            newYear = values[0];
        }

        return {
            make: newMake,
            model: newModel,
            year: newYear,
            vehicles: initialVehicles,
            showAddNew
        };
    }

    _loadOversizedFee({make, model, year}, hideShowNew = true) {
        const {
            facilityId
        } = this.props;
        const newState = {
            feeMessageLoading: false
        };

        this.setState({
            make,
            model,
            year,
            feeMessageLoading: true,
            feeError: null,
            showAddNew: !hideShowNew,
            showWarningTooltip: false,
            requiredError: null
        });

        this._feeLoader = APIUtils.makeCancelable(
            OversizedFeeAPI.getOversizedFee({facilityId, make, model, year})
        );
        this._feeLoader
            .promise
            .then(response => {
                const {
                    fee,
                    message,
                    not_allowed: notAllowed
                } = response.data.data.results;

                newState.fee = (fee) ? fee : 0;
                newState.feeMessage = (message) ? message : 'No oversized fees apply for this vehicle.';
                newState.vehicleNotAllowed = notAllowed;

                this._oversizedFeeLoaded(response, newState);
            })
            .catch(response => {
                newState.feeError = 'We are unable to check for oversized fees at this time. Please continue to checkout and update your vehicle information before you park.';

                this._oversizedFeeLoaded(response, newState);
            });
    }

    _oversizedFeeLoaded(response, newState) {
        if (this._isUnmounted) { return; }

        const {
            onOversizedFeeUpdated
        } = this.props;

        this.setState(newState);

        if (onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response,
                state: this.state
            });
        }
    }

    _setFeeError = feeError => {
        this.setState({
            feeError,
            apiFailed: true
        });
    }

    _renderDontKnowTooltip() {
        const {
            isParentVisible
        } = this.props;
        const {
            showWarningTooltip
        } = this.state;

        if (!isParentVisible || !showWarningTooltip) { return null; }

        return (
            <Tooltip
                className="VehicleList-dont-know-tooltip"
                position="right"
                triggerSelector=".VehicleList-dont-know"
                tipOffset={10}
                onHidden={this._onWarningTooltipHidden}
            >
                That&apos;s alright! Knowing your vehicle details before entering the garage can help with conversations about the oversize fees at the garage.
            </Tooltip>
        );
    }

    _renderOversizedDetails() {
        const {
            oversizedFeeType,
            phoneNumber,
            phoneNumberFriendly
        } = this.props;
        const measurement = (oversizedFeeType === 'class') ? 'size & class' : 'dimensions';

        if (this._isMobile) { return null; }

        return (
            <p>
                <small>Oversize fees and restrictions and are determined by vehicle {measurement} from CarQueryAPI.com data using vehicle make, model and year. Facilities may charge for any after-market addons to vehicles or assess and charge vehicles on a case-by-case basis. If you feel you are being charged for a vehicle that is not oversize, please give our Hero team a call at <a href={`tel:${phoneNumber}`}>{phoneNumberFriendly}</a>.</small>
            </p>
        );
    }

    _renderOversizedFeeMessage() {
        const {
            feeMessage,
            defaultFeeMessage,
            vehicleNotAllowed
        } = this.state;

        if (feeMessage && !isEmpty(feeMessage)) {
            return (
                <div className="content">
                    <strong>{feeMessage}</strong>
                    {!vehicleNotAllowed && (feeMessage !== 'No oversized fees apply for this vehicle.') && <small>Paid when you arrive at the location, not to SpotHero.</small>}
                </div>
            );
        } else {
            return (
                <div className="content">
                    <strong>Vehicle not found</strong> <small>{defaultFeeMessage.replace(/<br>/g, '. ')} may be charged at the spot.</small>
                </div>
            );
        }
    }

    _renderFeeMessageLoader() {
        const {
            feeMessageLoading
        } = this.state;

        if (!feeMessageLoading) { return null; }

        return (
            <div className="VehicleList-message">
                <Loader
                    size={20}
                    borderWidth={2}
                />
            </div>
        );
    }

    _renderOversizedFee() {
        const {
            fee,
            feeMessageLoading,
            vehicleNotAllowed,
            feeError
        } = this.state;

        return (
            <Fragment>
                {!fee && feeError && !feeMessageLoading &&
                    <p>{feeError}</p>
                }
                {(fee || fee === 0) && !feeMessageLoading &&
                    <div className="VehicleList-message">
                        <Alert
                            size="lg"
                            type={(vehicleNotAllowed) ? 'danger' : 'muted'}
                        >
                            {this._renderOversizedFeeMessage()}
                        </Alert>
                        {this._renderOversizedDetails()}
                    </div>
                }
            </Fragment>
        );
    }

    _renderVehicleList() {
        const {
            user,
            required,
            newVehicleFormErrors,
            checkOversized,
            showSaveAsDefault,
            allowManualEntry
        } = this.props;
        const {
            edit,
            showAddNew,
            vehicles
        } = this.state;

        if (edit) {
            return (
                <Fragment>
                    {(vehicles.length > 1) &&
                        <Fragment>
                            <Label>Vehicle</Label>
                            {checkOversized &&
                                <Alert type="highlight">This spot may charge fees onsite for oversized vehicles. Enter vehicle details to see estimated fees.</Alert>
                            }
                            <FormGroup>
                                <RadioGroup
                                    id="VehicleList-vehicle-options"
                                    name="vehicle"
                                    required={required}
                                    error={<FormElementError>{this._requiredErrorMessage}</FormElementError>}
                                    onChange={this._onVehicleChange}
                                >
                                    {map(vehicles, (vehicle, i) => {
                                        return (
                                            <Radio
                                                key={i}
                                                label={(
                                                    <Label className={vehicle.className}>
                                                        {vehicle.name &&
                                                            <span className="Radio-item-name">{vehicle.name}</span>
                                                        }
                                                        {vehicle.label}
                                                    </Label>
                                                )}
                                                value={vehicle.value}
                                                defaultChecked={vehicle.checked}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormGroup>
                        </Fragment>
                    }
                    {showAddNew &&
                        <FormGroup>
                            <AddNewVehicleForm
                                ref={node => { this._newVehicleForm = node; }}
                                user={user}
                                formErrors={newVehicleFormErrors}
                                onSelectChange={this._onNewVehicleSelectChange}
                                onVehicleDataRetrieved={this._onVehicleDataRetrieved}
                                setOversizeFeeError={this._setFeeError}
                                showSaveAsDefault={showSaveAsDefault}
                                useVehicleSelectLabels={!this._isMobile}
                                allowManualEntry={allowManualEntry}
                            />
                        </FormGroup>
                    }
                    {checkOversized && this._renderOversizedFee()}
                    {checkOversized && this._renderFeeMessageLoader()}
                    {this._renderDontKnowTooltip()}
                </Fragment>
            );
        } else {
            return (
                <Alert
                    className="VehicleList-callout"
                    size="md"
                >
                    This spot may charge oversize fees. <TextButton onClick={this._onEditClick}>Does this apply to me?</TextButton>
                </Alert>
            );
        }
    }

    submit() {
        const {
            onIDontKnowChanged
        } = this.props;
        const {
            showAddNew,
            iDontKnowChecked,
            make,
            model,
            year,
            apiFailed
        } = this.state;

        if (apiFailed) {
            return true;
        }

        let newFormData = null;
        let newMake = make;
        let newModel = model;
        let newYear = year;
        let saveVehicleAsDefault = false;

        if (showAddNew) {
            newFormData = this._newVehicleForm.submit();
            newMake = newFormData.make;
            newModel = newFormData.model;
            newYear = newFormData.year;
            saveVehicleAsDefault = newFormData.saveVehicleAsDefault;
        }

        if (!(newMake && newModel && newYear) && !iDontKnowChecked) {
            this.setState({
                requiredError: this._requiredErrorMessage
            });

            return false;
        }

        if (onIDontKnowChanged) {
            onIDontKnowChanged(iDontKnowChecked);
        }

        return {
            vehicleMake: newMake,
            vehicleModel: newModel,
            vehicleYear: newYear,
            saveVehicleAsDefault
        };
    }

    render() {
        const {
            highlight,
            requiredError,
            vehicles
        } = this.state;
        const classes = classNames(
            'VehicleList',
            {'VehicleList-no-vehicles': (vehicles.length === 1)},
            {highlight},
            {ContainsErrors: requiredError}
        );

        return (
            <Fragment>
                <FormGroup className={classes}>
                    {this._renderVehicleList()}
                </FormGroup>
                {requiredError &&
                    <FormGroupError>
                        {requiredError}
                    </FormGroupError>
                }
            </Fragment>
        );
    }
}
